import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    createCurrencyFormatter,
    createDateFormatter,
    createInternalLinkFormatter,
    createOptionsFormatter,
    createTimeRangeFormatter,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { INVOICE_STATUS } from '@/modules/Invoice/constants'
import { getServiceInvoices } from '@/modules/Service/services'
import { ServiceInvoice, ServiceReceiptProps } from '@/modules/Service/types'
import { Receipt } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const ServiceInvoices: React.FC<ServiceReceiptProps> = ({ serviceID }) => {
    const { t } = useTranslation()

    const {
        data: invoices = [],
        renderState,
        isFetching,
    } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.SERVICES, serviceID, 'invoices'],
            queryFn: async () => {
                const res = await getServiceInvoices({
                    serviceID: Number(serviceID),
                    page: 0,
                    pageSize: 100,
                })
                return res?.results ?? []
            },
        },
        {
            showLoading: true,
            showError: true,
        }
    )

    const columns = useMemo(
        (): DataTableColumn<ServiceInvoice>[] => [
            {
                key: 'invoice_id',
                title: 'Invoice ID',
                width: 120,
                formatter: createInternalLinkFormatter<ServiceInvoice>({
                    pathKey: 'invoiceDetail',
                    pathParams: (_value, row) => ({ id: row.invoice_id }),
                    label: (_value, row) => `INV - ${row.invoice_id}`,
                }),
            },
            {
                key: 'invoice_description',
                title: t('invoice_description'),
                width: 300,
            },
            {
                key: 'invoice_amount',
                title: t('invoice_amount'),
                width: 150,
                formatter: createCurrencyFormatter({ currency: 'VND' }),
            },
            {
                key: 'invoice_total',
                title: t('invoice_total'),
                width: 150,
                formatter: createCurrencyFormatter({ currency: 'VND' }),
            },
            {
                key: 'invoice_apply_startdate',
                title: t('invoice_apply_startdate'),
                width: 220,
                formatter: createTimeRangeFormatter({
                    startKey: 'invoice_apply_startdate',
                    endKey: 'invoice_apply_enddate',
                    format: 'DD/MM/YYYY',
                }),
            },
            {
                key: 'invoice_status',
                title: t('invoice_status'),
                width: 150,
                formatter: createOptionsFormatter({
                    options: INVOICE_STATUS(t),
                }),
            },
            {
                key: 'created_at',
                title: t('created_at'),
                width: 150,
                formatter: createDateFormatter('HH:mm DD/MM/YYYY'),
            },
            {
                key: 'invoice_duedate',
                title: t('invoice_duedate'),
                width: 150,
                formatter: createDateFormatter('DD/MM/YYYY'),
            },
            {
                key: 'invoice_payment_at',
                title: t('invoice_payment_at'),
                width: 150,
                formatter: createDateFormatter('DD/MM/YYYY'),
            },
        ],
        [t]
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <Paper className="mt-2">
            <p className="flex items-center gap-2 mb-3 font-medium text-primary">
                <Receipt className="h-4 w-4" />
                {t('service-receipt')}
            </p>
            <DataTableCustomized<ServiceInvoice>
                columns={columns}
                rows={invoices}
                rowKey={(row) => String(row.invoice_id)}
                isFetching={isFetching}
                emptyMessage={t('no-data')}
                bordered
                size="small"
                pageSize={5}
            />
        </Paper>
    )
}

export default ServiceInvoices
